import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import * as Sentry from '@sentry/browser';
import {NavLink, useHistory} from 'react-router-dom';
import styled from 'styled-components';
import type {MissionControlHeader_viewer$data as MissionControlHeader_viewer} from './__generated__/MissionControlHeader_viewer.graphql';
import Logo, {LOGO_SIZE} from '../../../forecast-app/shared/components/logo/Logo';
import {useSingleton} from '../../../forecast-app/shared/hooks/useSingleton';
import {featureFlagUiPathFromStorage} from '../feature-flags/featureFlagUiDestination';
import {FEATURE_FLAG_ENTRY_PATH} from '../feature-flags/featureFlagUiPreference';

export const LogoWrapper = styled.div`
	display: inline-block;
	height: 2.6rem;
	padding: 0.75rem 0;
	margin: 0 2rem 0 1rem;
	vertical-align: middle;
`;

/** The one company Mission Control belongs to. Everyone else sees only the impersonate tab. */
const FORECAST_COMPANY_ID = 'Q29tcGFueTox';

function goto(url: string, history): void {
	history.push(url);
}

function getButton(url: string, text: string, index: number, history): JSX.Element {
	const currentUrl = window.location.pathname;
	return (
		<button key={index} className={'tab' + (url === currentUrl ? ' selected' : '')} onClick={() => goto(url, history)}>
			{text}
		</button>
	);
}

interface MissionControlHeaderProps {
	viewer: MissionControlHeader_viewer;
}

function MissionControlHeader({viewer}: MissionControlHeaderProps): JSX.Element {
	useSingleton(() => {
		Sentry.configureScope(scope => {
			scope.setUser({
				email: viewer.email || undefined,
				id: atob(viewer.actualPersonId || '').replace('Person:', ''),
				company_id: atob(viewer.company?.id || '').replace('Company:', ''),
				name: viewer.firstName + ' ' + viewer.lastName,
				company_name: viewer.company?.name,
			});
		});
	}, []);

	const history = useHistory();

	let urls = [
		'',
		'/company-stats',
		'/trials',
		'/companies-with-modules',
		'/ai-credits',
		'/person-list',
		'/features',
		'/ml-stats',
		'/restore-data',
		'/sage-signup',
		'/nova-studio',
		'/fly-previews',
	];
	let texts = [
		'Impersonate',
		'Company stats',
		'Trials',
		'Modules',
		'AI credits',
		'Person list',
		'Feature Flags',
		'ML Stats',
		'Restore Data',
		'Sage Signup',
		'Nova Studio',
		'Fly previews',
	];

	// Every tab but impersonation is Forecast's own tooling, so on production nobody outside Forecast is
	// offered them. The pages themselves are gated too — by their own `component(...)` query field and by
	// the internal-company guard each Mission Control endpoint applies — so this is about not showing an
	// operator doors they cannot open, not about keeping them shut.
	if (process.env.CIRCLE_BRANCH === 'production' && viewer.company?.id !== FORECAST_COMPANY_ID) {
		urls = [''];
		texts = ['Impersonate'];
	}

	const prefix = window.location.pathname.includes('missioncontrol') ? '/missioncontrol' : '/control';
	// The feature flags tab goes straight to whichever of the two screens is currently chosen, rather
	// than to the entry point that would only redirect there. A click whose destination is the screen the
	// operator is already on would otherwise push the entry route and then try to leave it again in the
	// same navigation, which lands on a blank page.
	const resolveUrl = (url: string): string =>
		prefix + (url === FEATURE_FLAG_ENTRY_PATH ? featureFlagUiPathFromStorage() : url);
	return (
		<div className="mission-control-header">
			<NavLink aria-label="homepage" to="">
				<LogoWrapper>
					<Logo size={LOGO_SIZE.SMALL} />
				</LogoWrapper>
			</NavLink>
			{urls.map((url, index) => getButton(resolveUrl(url), texts[index], index, history))}
		</div>
	);
}

export default createFragmentContainer(MissionControlHeader, {
	viewer: graphql`
		fragment MissionControlHeader_viewer on Viewer {
			email
			actualPersonId
			firstName
			lastName
			company {
				id
				name
			}
		}
	`,
});
